import React from "react";
import { useNavigate } from "react-router-dom";
import { useSelector } from "react-redux";

import PageTitle from "../../layouts/PageTitle";
import { useGetImprestSummaryQuery } from "../../../store/appApi";
import { selectUserRole } from "../../../store/authSlice";
import { formatCurrency, formatDateLabel, formatRoleLabel } from "./imprestConfig";

const panelClass =
  "rounded-3xl border border-gray-200 bg-white shadow-xl shadow-slate-200/70";

const balanceClass = (value) => {
  if (value > 0) return "text-emerald-600";
  if (value < 0) return "text-rose-600";
  return "text-gray-500";
};

const ImprestAdminList = () => {
  const navigate = useNavigate();
  const role = useSelector(selectUserRole);
  const { data, isLoading, isFetching } = useGetImprestSummaryQuery(undefined, {
    skip: role !== "super-admin",
  });

  if (role !== "super-admin") {
    return (
      <>
        <PageTitle activeMenu="All Employees" motherMenu="Imprest" />
        <div className="rounded-3xl border border-gray-200 bg-white p-6 text-center text-gray-500 shadow-xl shadow-slate-200/70">
          Only a super-admin can view every employee's imprest balances.
        </div>
      </>
    );
  }

  const employees = Array.isArray(data?.employees) ? data.employees : [];
  const summary = data?.summary || {};
  const loading = isLoading || isFetching;

  return (
    <>
      <PageTitle activeMenu="All Employees" motherMenu="Imprest" />

      <div className="space-y-6">
        <section className={`${panelClass} relative overflow-hidden p-6`}>
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_top_right,_rgba(169,126,8,0.10),_transparent_35%),radial-gradient(circle_at_bottom_left,_rgba(226,232,240,0.6),_transparent_40%)]" />
          <div className="relative flex flex-wrap items-start justify-between gap-4">
            <div>
              <div className="inline-flex rounded-full border border-amber-200 bg-amber-50 px-3 py-1 text-[11px] font-semibold uppercase tracking-[0.22em] text-amber-700">
                Imprest
              </div>
              <h1 className="mt-4 text-3xl font-semibold text-gray-900 md:text-4xl">
                All Employees
              </h1>
              <p className="mt-2 max-w-2xl text-sm text-gray-500">
                Every employee holding imprest cash on this project. Click a
                row to open their full ledger.
              </p>
              <div className="mt-4 flex flex-wrap gap-2">
                <button
                  type="button"
                  className="rounded-full border-2 border-amber-500 px-5 py-2.5 text-xs font-semibold uppercase tracking-[0.2em] text-gray-700 transition hover:text-amber-700"
                  onClick={() => navigate("/imprest/expenses-report")}
                >
                  Expenses Report
                </button>
              </div>
            </div>

            <div className="grid min-w-[320px] grid-cols-3 gap-3 text-center text-xs text-gray-600">
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Total Allotted</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(summary.totalAllotted)}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Total Expensed</div>
                <div className="mt-1 text-lg font-semibold text-gray-900">
                  {formatCurrency(summary.totalExpensed)}
                </div>
              </div>
              <div className="rounded-2xl border border-gray-200 bg-gray-50 px-3 py-3">
                <div className="text-gray-400">Total Balance</div>
                <div
                  className={`mt-1 text-lg font-semibold ${balanceClass(Number(summary.balance) || 0)}`}
                >
                  {formatCurrency(summary.balance)}
                </div>
              </div>
            </div>
          </div>
        </section>

        <section className={panelClass}>
          <div className="overflow-x-auto">
            <table className="min-w-full divide-y divide-gray-200 text-sm">
              <thead className="bg-gray-50 text-gray-500">
                <tr>
                  <th className="px-5 py-2.5 text-left font-medium">Employee</th>
                  <th className="px-5 py-2.5 text-left font-medium">Role</th>
                  <th className="px-5 py-2.5 text-right font-medium">Allotted</th>
                  <th className="px-5 py-2.5 text-right font-medium">Expensed</th>
                  <th className="px-5 py-2.5 text-right font-medium">Balance</th>
                  <th className="px-5 py-2.5 text-left font-medium">Last Entry</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-gray-200 bg-white">
                {loading && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-gray-500">
                      Loading imprest balances...
                    </td>
                  </tr>
                )}

                {!loading && employees.length === 0 && (
                  <tr>
                    <td colSpan={6} className="px-5 py-10 text-center text-gray-500">
                      No imprest allotted yet.
                    </td>
                  </tr>
                )}

                {!loading &&
                  employees.map((employee) => (
                    <tr
                      key={employee.employeeId}
                      className="cursor-pointer transition hover:bg-gray-50"
                      onClick={() => navigate(`/imprest/admin/${employee.employeeId}`)}
                    >
                      <td className="px-5 py-2.5 font-semibold text-gray-900">
                        {employee.employeeName || "—"}
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {formatRoleLabel(employee.employeeRole)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-900">
                        {formatCurrency(employee.totalAllotted)}
                      </td>
                      <td className="px-5 py-2.5 text-right text-gray-900">
                        {formatCurrency(employee.totalExpensed)}
                      </td>
                      <td
                        className={`px-5 py-2.5 text-right font-semibold ${balanceClass(Number(employee.balance) || 0)}`}
                      >
                        {formatCurrency(employee.balance)}
                      </td>
                      <td className="px-5 py-2.5 text-gray-600">
                        {formatDateLabel(employee.lastEntryDate)}
                      </td>
                    </tr>
                  ))}
              </tbody>
            </table>
          </div>
        </section>
      </div>
    </>
  );
};

export default ImprestAdminList;
